import React, { useState, useMemo } from 'react';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiCalendar, FiUser, FiClock, FiDollarSign, FiFilter, FiCheckCircle, FiAlertCircle } = FiIcons;

const TutorIndividualLog = ({ tutorName, onBack }) => {
  const { sessions, students, months, formatCurrency, appSettings } = useData();
  const [selectedMonth, setSelectedMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'paid' | 'unpaid'

  const tutorSessions = useMemo(() => {
    const list = [];
    Object.keys(sessions).forEach(studentId => {
      const student = students.find(s => s.id === studentId);
      const monthData = sessions[studentId][selectedMonth] || [];
      
      monthData.forEach(session => {
        if (session.tutor === tutorName) {
          list.push({
            ...session,
            studentName: student?.studentName || 'Unknown Student',
            grade: student?.grade || '-'
          });
        }
      });
    });
    return list.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [sessions, students, selectedMonth, tutorName]);

  const filteredSessions = tutorSessions.filter(s => {
    if (statusFilter === 'paid') return s.isPaid;
    if (statusFilter === 'unpaid') return !s.isPaid;
    return true;
  });

  const stats = useMemo(() => {
    const totalMins = filteredSessions.reduce((acc, s) => acc + (parseInt(s.duration) || 0), 0);
    const totalRev = filteredSessions.reduce((acc, s) => acc + (parseFloat(s.feePerSession) || 0), 0);
    return {
      count: filteredSessions.length,
      hours: (totalMins / 60).toFixed(1),
      revenue: totalRev,
      students: new Set(filteredSessions.map(s => s.studentId)).size
    };
  }, [filteredSessions]);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm"
          >
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <div>
            <h2 className="text-3xl font-black text-gray-900 tracking-tight">{tutorName}</h2>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Individual Session Breakdown</p>
          </div>
        </div>

        <div className="flex gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-48">
            <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <select 
              value={selectedMonth} 
              onChange={e => setSelectedMonth(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-gray-100 rounded-2xl focus:border-indigo-600 outline-none font-bold text-sm text-gray-700 shadow-sm appearance-none cursor-pointer"
            >
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="relative flex-1 md:w-40">
            <SafeIcon icon={FiFilter} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <select 
              value={statusFilter} 
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-gray-100 rounded-2xl focus:border-indigo-600 outline-none font-bold text-sm text-gray-700 shadow-sm appearance-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid Only</option>
              <option value="unpaid">Unpaid Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Mini Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Classes', value: stats.count, icon: FiCalendar, color: 'indigo' },
          { label: 'Hours', value: stats.hours, icon: FiClock, color: 'blue' },
          { label: 'Students', value: stats.students, icon: FiUser, color: 'emerald' },
          { label: 'Value', value: formatCurrency(stats.revenue), icon: FiDollarSign, color: 'orange' }
        ].map((item, i) => (
          <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm">
            <div className={`w-10 h-10 rounded-xl bg-${item.color}-50 text-${item.color}-600 flex items-center justify-center mb-4`}>
              <SafeIcon icon={item.icon} className="text-lg" />
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{item.label}</p>
            <p className="text-xl font-black text-gray-900">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date & Time</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Student</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Subject</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Duration</th>
                <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Fee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredSessions.length > 0 ? (
                filteredSessions.map((session, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/80 transition-colors group">
                    <td className="px-8 py-5">
                      <div className="text-sm font-bold text-gray-900">{new Date(session.date).toLocaleDateString(undefined, { dateStyle: 'medium' })}</div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">{session.time}</div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="text-sm font-bold text-gray-900">{session.studentName}</div>
                      <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Grade {session.grade}</div>
                    </td>
                    <td className="px-8 py-5">
                      <span className="inline-flex px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold">
                        {session.subject}
                      </span>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-600">
                        <SafeIcon icon={FiClock} className="text-gray-300" />
                        {session.duration}m
                      </div>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-black text-gray-900">{formatCurrency(session.feePerSession)}</span>
                        <div className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-widest ${session.isPaid ? 'text-emerald-500' : 'text-orange-500'}`}>
                          <SafeIcon icon={session.isPaid ? FiCheckCircle : FiAlertCircle} />
                          {session.isPaid ? 'Paid' : 'Due'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-8 py-20 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiCalendar} className="text-2xl text-gray-300" />
                    </div>
                    <p className="text-gray-900 font-bold">No sessions found</p>
                    <p className="text-sm text-gray-400 mt-1">Try changing the month or status filter.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TutorIndividualLog;